/**
 * Resolving the CV a skill run writes into, shared by the native run sheet and
 * the web run dialog so the two can never drift.
 *
 * The run screens' CV picker always offers a synthetic "Main" row whose id is
 * the `master` sentinel (see `cv-main-default.ts`), and it is preselected for
 * every user who has not designated a Main CV. `master` is a route/tool alias,
 * not a CV row: sending it to `POST /api/skills/:id/run` makes the server
 * resolve it through the designated-main preference, which is null for those
 * users. The run pipeline then silently downgrades to a dry run
 * (`skills.service.ts`: "a profile-backed Main supplies truthful input context,
 * but there is no CV row for the skill writer to overwrite"), so the skill
 * generates a full CV, writes nothing, returns `resultCvId: null` - and the run
 * screen reports "No changes needed" over a metered run that did real work.
 *
 * A CV skill can only write into a concrete CV, so the run screens materialise
 * one first, exactly as the Create CV flow already does for the same sentinel
 * (`use-create-cv-submit.ts`, the `skill_run` plan). This module holds the pure
 * decision; the callers own the IO and the cleanup of a copy a failed run left
 * behind.
 */
import { MASTER_CV_ID } from '../../lib/cv-main-default';
import type { SkillRunSpec } from './skill-run-spec';

export interface SkillRunCvTargetDecision {
  /**
   * True when the picked target must become a real CV row before the run.
   * Only a CV-writing skill needs this: a text skill never writes, so the
   * `master` sentinel stays a read-only profile context for it and
   * materialising a CV would litter the user's list with an unwanted copy.
   */
  materializeMaster: boolean;
  /** The id to send as-is when nothing has to be materialised. */
  cvId: string | undefined;
}

/**
 * Decide what a run should send as `cvId`, given the picker's selection.
 *
 * `selected` is whatever the picker holds: a concrete CV id, the `master`
 * sentinel, or null/undefined when the skill takes no CV at all.
 */
export function resolveSkillRunCvTarget(params: {
  selected: string | null | undefined;
  spec: Pick<SkillRunSpec, 'outputKind'>;
}): SkillRunCvTargetDecision {
  const { selected, spec } = params;
  const writesCv = spec.outputKind === 'cv_edit';
  if (selected === MASTER_CV_ID && writesCv) {
    return { materializeMaster: true, cvId: undefined };
  }
  return { materializeMaster: false, cvId: selected ?? undefined };
}
